import { useMemo, useState } from "react"
import { Box, Button, Flex, HStack, Input, Link, Text } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import SectionCard from "../../framework/SectionCard"
import DataTable from "../../framework/DataTable"
import { formatDateTime } from "../../utils/datetime"
import { usePlaces, type PlaceRow } from "../../hooks/usePlaces"
import ErrorAlert from "../../components/ErrorAlert"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { Link as RouterLink } from "react-router-dom"

const columns: ColumnDef<PlaceRow>[] = [
  {
    accessorKey: "name",
    header: "Name",
    id: "name",
    enableSorting: true,
    cell: ({ row }) => {
      const value = row.original.name || row.original.id
      return value ? (
        <Link
          asChild
          color="blue.200"
          fontWeight="semibold"
          _hover={{ textDecoration: "underline" }}
        >
          <RouterLink to={`/places/${row.original.id}`}>{value}</RouterLink>
        </Link>
      ) : (
        "—"
      )
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    id: "type",
    enableSorting: true,
    cell: ({ getValue }) => getValue<string>() || "—",
  },
  {
    accessorKey: "id",
    header: "ID",
    id: "id",
    enableSorting: true,
    cell: ({ getValue }) => (
      <Text fontFamily="mono" fontSize="sm">
        {getValue<string>()}
      </Text>
    ),
  },
  {
    accessorKey: "osmRef",
    header: "OSM Ref",
    id: "osmRef",
    enableSorting: true,
    cell: ({ getValue }) => {
      const ref = getValue<string>()
      if (!ref) return "—"

      const url = ref.includes("/") ? `https://www.openstreetmap.org/${ref}` : `https://www.openstreetmap.org/node/${ref}`

      return ref ? (
        <Link
          href={url}
          target="_blank"
          rel="noreferrer"
          color="blue.200"
        >
          {ref}
        </Link>
      ) : (
        "—"
      )
    },
  },
  {
    accessorKey: "latitude",
    header: "Lat",
    id: "latitude",
    enableSorting: true,
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>()
      return value === undefined ? "—" : value.toFixed(4)
    },
  },
  {
    accessorKey: "longitude",
    header: "Lng",
    id: "longitude",
    enableSorting: true,
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>()
      return value === undefined ? "—" : value.toFixed(4)
    },
  },
  {
    accessorKey: "updatedAt",
    header: "Updated",
    id: "updatedAt",
    enableSorting: true,
    cell: ({ getValue }) => formatDateTime(getValue<string>()),
  },
]

export default function PlaceList() {
  const [globalFilter, setGlobalFilter] = useState("")
  const [sorting, setSorting] = useState([{ id: "name", desc: false }])

  const currentSort = sorting[0] ?? { id: "name", desc: false }

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePlaces({
    filter: globalFilter || undefined,
    sort: currentSort.id,
    dir: currentSort.desc ? "desc" : "asc",
    limit: 100,
  })

  const places = useMemo(
    () => data?.pages.flatMap((page) => page.places ?? []).filter((place): place is PlaceRow => Boolean(place)) ?? [],
    [data]
  )

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Text fontSize="md" mb="3">
        Places
      </Text>
      <HStack gap="3" mb="4" flexWrap="wrap">
        <Input
          placeholder="Filter places"
          bg="gray.700"
          value={globalFilter}
          onChange={(event) => setGlobalFilter(event.target.value)}
          flex="1"
          minW="240px"
        />
        <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
          {places.length} places
        </Text>
      </HStack>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

      <Box overflowX="auto">
        <DataTable
          columns={columns}
          data={places}
          isLoading={isLoading}
          emptyMessage="No places available."
          sorting={sorting}
          onSortingChange={setSorting}
          globalFilter={globalFilter}
          manualSorting={true}
        />
      </Box>

      {hasNextPage && (
        <Flex justify="flex-end" mt="4">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </Flex>
      )}
    </SectionCard>
  )
}
